/**
 * Generation with AI (document/64). Shapes from `packages/contracts/src/ai-generation.ts`,
 * paths from `apps/gateway/src/routes/ai-generation.ts`. The interface never
 * talks to ai.miglioresoftware.com: it asks the gateway, which holds the key.
 */
import type {
  AiCtaRequest,
  AiCtaResult,
  AiGenerationRecord,
  AiImageRequest,
  AiImageResult,
  AiJobView,
  AiProposedSection,
  AiPurpose,
  AiSectionProposal,
  AiVideoRequest,
  WriteResult,
} from '@cms/contracts'
import { apiFetch } from './client.js'

export interface AiAccountChoice {
  id: string
  label: string
  provider?: string
  own?: boolean
  status?: string
  reason?: string
}

export interface AiAccountList {
  accounts: AiAccountChoice[]
  /** True when AI could not be asked: the default account is still used. */
  degraded: boolean
  reason?: string
}

export function listAiAccountsFor(purpose: AiPurpose): Promise<AiAccountList> {
  return apiFetch('/ai/accounts', { query: { purpose } })
}

export function generateImage(siteId: string, body: AiImageRequest, idempotencyKey: string): Promise<AiImageResult> {
  return apiFetch(`/sites/${encodeURIComponent(siteId)}/ai/images`, { method: 'POST', body, idempotencyKey })
}

export function getMediaGeneration(assetId: string): Promise<{ assetId: string; generation: AiGenerationRecord | null }> {
  return apiFetch(`/media/${encodeURIComponent(assetId)}/generation`)
}

export function startVideo(siteId: string, body: AiVideoRequest, idempotencyKey: string): Promise<AiJobView> {
  return apiFetch(`/sites/${encodeURIComponent(siteId)}/ai/videos`, { method: 'POST', body, idempotencyKey })
}

export function getAiJob(jobId: string): Promise<AiJobView> {
  return apiFetch(`/ai/jobs/${encodeURIComponent(jobId)}`)
}

export function listAiJobs(siteId: string): Promise<{ items: AiJobView[] }> {
  return apiFetch(`/sites/${encodeURIComponent(siteId)}/ai/jobs`, { query: { limit: 10 } })
}

export function generateCta(body: AiCtaRequest, idempotencyKey?: string): Promise<AiCtaResult> {
  return apiFetch('/ai/cta', { method: 'POST', body, idempotencyKey })
}

export function proposeSections(pageId: string, body: { brief: string; accountId?: string }): Promise<AiSectionProposal> {
  return apiFetch(`/pages/${encodeURIComponent(pageId)}/ai/sections`, { method: 'POST', body })
}

export function regenerateSection(
  pageId: string,
  body: { brief: string; section: Pick<AiProposedSection, 'sectionKey' | 'props'>; instruction?: string; accountId?: string },
): Promise<{ section: AiProposedSection; accountUsed: string }> {
  return apiFetch(`/pages/${encodeURIComponent(pageId)}/ai/sections/regenerate`, { method: 'POST', body })
}

export function acceptSections(
  pageId: string,
  body: { sections: AiProposedSection[]; mode: 'append' | 'replace'; accountUsed?: string; reason: string; expectedVersion: number },
  idempotencyKey: string,
): Promise<WriteResult & { sectionIds: string[] }> {
  return apiFetch(`/pages/${encodeURIComponent(pageId)}/ai/sections/accept`, { method: 'POST', body, idempotencyKey })
}
